"use client";

import React, { useState } from "react";
import Image from "next/image";

type FaqItem = {
  question: string;
  answer: string;
};

type FaqProps = {
  categoryName: string;
  areaName?: string; // Optional
};

const getFaqs = (category: string, area?: string): FaqItem[] => {
  const categoryLC = category.toLowerCase();
  const areaLC = area?.toLowerCase();

  const coverageHTML =
    !area || area.toLowerCase() === "terdekat"
      ? `<a href="#coverage" title="Area Cakupan" class="text-purple-600 underline hover:text-purple-800 transition-colors">terdekat</a>`
      : area;

  const inject = (text: string) =>
    text
      .replace(/\{coverage\}/gi, coverageHTML)
      .replace(/\{kategori\}/gi, categoryLC);

  return [
    {
      question: `Berapa lama durasi rental {kategori}?`,
      answer: `Durasi sewa minimum adalah <strong>1 hari</strong>, namun untuk penyewaan jangka lebih panjang, kami menyediakan harga khusus yang jauh lebih hemat. Semakin lama durasi sewa, semakin efisien biayanya. Silakan konsultasikan kebutuhan Anda agar kami bisa bantu dengan penawaran terbaik.`,
    },
    {
      question: `Berapa harga sewa {kategori}?`,
      answer: `
    Harga sewa {kategori} bersifat fleksibel dan disesuaikan dengan beberapa faktor seperti <strong>spesifikasi unit</strong>, <strong>jumlah yang dibutuhkan</strong>, <strong>durasi sewa</strong>, serta <strong>lokasi pengiriman di luar area {coverage}</strong>. Kami juga secara berkala menawarkan <strong>promo khusus</strong> untuk penyewaan {kategori}.<br />
    Silakan hubungi tim kami untuk mendapatkan penawaran yang paling sesuai dan kompetitif sesuai kebutuhan Anda.`,
    },
    {
      question: `Apa bisa sewa {kategori} untuk pribadi/personal?`,
      answer: `Bisa, tentu saja. Meskipun sebagian besar klien kami berasal dari kalangan bisnis dan korporat, kami tetap melayani kebutuhan sewa {kategori} untuk keperluan pribadi, selama sesuai dengan ketentuan dan ketersediaan.`,
    },
    {
      question: `Apakah ada biaya pengiriman ke {coverage}?`,
      answer: `
    Pengiriman ke area {coverage} <strong>GRATIS</strong>.<br />
    Di luar area tersebut, ada biaya tambahan sesuai jarak dan kebutuhan.<br />
    Jika diperlukan, kami juga bisa kirim dengan teknisi profesional.
  `,
    },
    {
      question: `Bagaimana cara booking sewa {kategori}?`,
      answer: `Khusus untuk daerah {coverage} kamu bisa langsung <strong>kontak kami via WhatsApp</strong> atau isi <strong>form pemesanan rental laptop dan perangkat IT</strong> di website grasitech.id atau di website <a class="text-purple-500 underline" href="https://grasitech.com" target="_blank" rel="noopener noreferrer" aria-label="Kunjungi grasitech.com untuk pemesanan rental laptop dan perangkat lainnya">grasitech.com</a> untuk memulai proses booking dengan mudah dan cepat`,
    },
    {
      question: `{kategori} yang disewa garansi apa?`,
      answer: `
    Semua {kategori} yang kami sewakan dilengkapi dengan <strong>garansi teknis selama masa sewa</strong> untuk memastikan kenyamanan dan kelancaran operasional Anda.<br />
    Jika terjadi kendala teknis, kami siap memberikan <strong>penggantian unit</strong> atau <strong>perbaikan cepat</strong> tanpa biaya tambahan.<br />
    Seluruh unit kami rutin dicek dan dirawat secara berkala oleh tim teknisi berpengalaman, sehingga siap pakai tanpa drama.<br />
    Pengiriman juga akan kami lakukan <strong>tepat waktu</strong> agar sesuai dengan jadwal Anda.<br />
    Fokus kami: <em>Anda tinggal pakai, kami yang urus sisanya</em>.
  `,
    },
    {
      question: `Apa saja syarat sewa {kategori}?`,
      answer: `
        <ol class="list-decimal list-inside space-y-1 mt-2">
          <li>Foto identitas (KTP).</li>
          <li>NPWP perusahaan/instansi.</li>
          <li>Surat Pesanan (PO) atau surat permintaan resmi.</li>
          <li>Pembayaran saat barang diterima.</li>
        </ol>
      `,
    },
  ].map(({ question, answer }) => ({
    question: inject(question),
    answer: inject(answer),
  }));
};

export default function Faq({ categoryName, areaName }: FaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = getFaqs(categoryName, areaName);

  const toggleIndex = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="globalFaq" className="scroll-mt-50">
      <div className="max-w-[1170px] w-full mx-auto px-4 sm:px-8 xl:px-0 mb-20">
        <div className="mb-10">
          <span className="flex items-center gap-2.5 font-medium text-dark mb-1.5">
            <Image src="/images/icons/icon-07.svg" alt="icon" width={17} height={17} />
            Seputar Rental {categoryName}
          </span>
          <h2 className="font-semibold text-xl xl:text-heading-6 text-dark">
            Yang sering ditanyakan seputar Sewa {categoryName}
            {areaName && ` di ${areaName}`}
          </h2>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border rounded-md p-4 cursor-pointer bg-white shadow hover:shadow-md transition"
              onClick={() => toggleIndex(i)}
              aria-expanded={openIndex === i}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleIndex(i)}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium" dangerouslySetInnerHTML={{ __html: faq.question }} />
                <span className="text-xl select-none">{openIndex === i ? "−" : "+"}</span>
              </div>
              {openIndex === i && (
                <p className="mt-2 text-custom-sm text-gray-700" dangerouslySetInnerHTML={{ __html: faq.answer }} />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
